import React, { useRef } from 'react';
import { Tooltip, InfoIcon } from './Tooltip';

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  label: string;
  tooltip?: string;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;   // e.g. "£"
  suffix?: string;   // e.g. "GB" or "/month"
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  showStepper?: boolean;
}

export function NumberInput({
  value,
  onChange,
  label,
  tooltip,
  min = 0,
  max = Infinity,
  step = 1,
  prefix,
  suffix,
  placeholder,
  inputMode = 'decimal',
  showStepper = true,
}: NumberInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const clampValue = (next: number) => Math.max(min, Math.min(max, next));

  const handleBlur = () => {
    let val = parseFloat(inputRef.current?.value || '0');
    if (isNaN(val)) val = min;
    const clamped = clampValue(val);
    onChange(clamped);
    if (inputRef.current) inputRef.current.value = clamped.toString();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Allow empty string temporarily while typing, but handle numeric logic elsewhere
    if (val === '') return;
    const parsed = parseFloat(val);
    if (!isNaN(parsed)) {
      // Don't clamp yet, wait for blur or use a controlled update if needed.
      // For responsiveness, we update the parent but don't force formatting yet.
      onChange(parsed);
    }
  };

  const adjustValue = (direction: -1 | 1) => {
    const next = clampValue((Number.isFinite(value) ? value : 0) + direction * step);
    onChange(next);
    if (inputRef.current) inputRef.current.value = next.toString();
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-center gap-1 mb-1">
        <label className="text-[12px] text-azure-muted uppercase tracking-wider font-medium">
          {label}
        </label>
        {tooltip && (
          <Tooltip content={tooltip}>
            <InfoIcon />
          </Tooltip>
        )}
      </div>

      <div
        className="relative flex items-center w-full min-h-[44px] rounded-[6px] focus-within:border-azure-blue focus-within:border-[2px] transition-all duration-150"
        style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)' }}
      >
        {showStepper && (
          <button
            type="button"
            onClick={() => adjustValue(-1)}
            aria-label={`Decrease ${label}`}
            className="h-full min-w-[44px] px-3 text-[18px] text-azure-muted transition-colors hover:text-azure-text"
          >
            −
          </button>
        )}

        {prefix && (
          <span className="pl-3 text-[14px] text-azure-muted select-none">
            {prefix}
          </span>
        )}
        
        <input
          ref={inputRef}
          type="number"
          defaultValue={value}
          onChange={handleInputChange}
          onBlur={handleBlur}
          min={min}
          max={max}
          step={step}
          inputMode={inputMode}
          placeholder={placeholder}
          className="
            flex-1 h-full bg-transparent px-2
            text-[14px] text-azure-text font-mono
            focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
          "
        />

        {suffix && (
          <span className="pr-3 text-[14px] text-azure-muted select-none">
            {suffix}
          </span>
        )}

        {showStepper && (
          <button
            type="button"
            onClick={() => adjustValue(1)}
            aria-label={`Increase ${label}`}
            className="h-full min-w-[44px] px-3 text-[18px] text-azure-muted transition-colors hover:text-azure-text"
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}
